import { mockUsers } from '../data/mockUsers'
import type {
  CreateTaskPayload,
  Task,
  TaskReminderSettings,
  TaskUser,
  UpdateTaskPayload,
  TaskDashboardSummary
} from '../types/task'
import type { TranslationKey } from '../i18n'
import { api, type TaskCreatePayload, type TaskUpdatePayload, type TaskRecord } from './api'
import { loadTasks } from './storage'
import { ensureOfflineReady } from '../offline/initOffline'
import { getActiveTenantKey } from '../offline/tenant'
import { dexieTaskRepo } from '../offline/repos/DexieTaskRepo'
import { dexieActivityRepo } from '../offline/repos/DexieActivityRepo'
import { clearEntityChange, enqueueEntityChange } from '../offline/outbox/entityOutbox'
import { evaluateTaskClarity } from './taskClarity'
import {
  buildQueuedEntityMutationResult,
  readEntityBatchSyncStatus,
  retryEntityBatchSyncStatus,
  retryEntityBatchSyncStatusWithReport,
  syncedEntityMutationResult,
  type EntityBatchSyncRetryReport,
  type EntityBatchSyncStatus,
  type EntityMutationResult
} from './entityMutationSync'

type TaskStatusCode = 'overdue' | 'due_soon' | 'on_track' | 'completed' | 'blocked'

type TaskStatusDuration = {
  count: number
  unitKey: TranslationKey
}

export type TaskStatusInsight = {
  code: TaskStatusCode
  label: string
  labelKey?: TranslationKey
  description: string
  descriptionKey?: TranslationKey
  duration?: TaskStatusDuration
  palette: {
    background: string
    border: string
    color: string
  }
}

const taskStatusPalette: Record<TaskStatusCode, TaskStatusInsight['palette']> = {
  overdue: {
    background: '#fef2f2',
    border: '#fecaca',
    color: '#b91c1c'
  },
  due_soon: {
    background: '#fffbeb',
    border: '#fed7aa',
    color: '#b45309'
  },
  on_track: {
    background: '#f0f9ff',
    border: '#bae6fd',
    color: '#1d4ed8'
  },
  completed: {
    background: '#f0fdf4',
    border: '#bbf7d0',
    color: '#166534'
  },
  blocked: {
    background: '#eef2ff',
    border: '#c7d2fe',
    color: '#4338ca'
  }
}

type TaskValidationField = 'title' | 'description' | 'assigneeId' | 'dueAt' | 'parentId' | 'general'

export type TaskValidationIssue = {
  field: TaskValidationField
  message: string
  messageKey?: TranslationKey
  messageParams?: Record<string, string | number>
  severity: 'error' | 'warning'
}

export type TaskValidationResult = {
  issues: TaskValidationIssue[]
  hasErrors: boolean
}

export type TaskMutationResult<T> = EntityMutationResult<T>

type TaskValidationOptions = {
  tasks?: Task[]
  now?: number
}

const defaultReminder: TaskReminderSettings = {
  enabled: true,
  cadenceMinutes: 1440,
  remindCollaborators: false,
  remindResponsible: true
}

const clone = <T>(value: T): T => structuredClone(value)

const ensureReminderSettings = (value?: TaskRecord['reminderSettings']): TaskReminderSettings => ({
  enabled: value?.enabled ?? defaultReminder.enabled,
  cadenceMinutes: value?.cadenceMinutes ?? defaultReminder.cadenceMinutes,
  remindCollaborators: value?.remindCollaborators ?? defaultReminder.remindCollaborators,
  remindResponsible: value?.remindResponsible ?? defaultReminder.remindResponsible
})

const fromApiTask = (record: TaskRecord): Task => normalizeTask({
  id: record.id,
  title: record.title,
  description: record.description ?? undefined,
  assigneeId: record.assigneeId ?? undefined,
  collaboratorIds: record.collaboratorIds ?? [],
  dueAt: record.dueAt ?? undefined,
  completed: record.completed,
  completedAt: record.completedAt ?? undefined,
  createdAt: record.createdAt,
  updatedAt: record.updatedAt,
  parentId: record.parentId ?? undefined,
  reminderSettings: ensureReminderSettings(record.reminderSettings),
  lastReminderAt: record.lastReminderAt ?? undefined,
  lastNotifiedAt: record.lastNotifiedAt ?? undefined,
  tags: record.tags ?? [],
  metadata: record.metadata ?? undefined
})

type TaskListSource = 'remote' | 'cache'

let lastTaskListSource: TaskListSource | null = null

const rosterCacheKey = (tenantKey: string) => `tasks:userRoster::${tenantKey}`

const readRosterCache = (tenantKey: string): TaskUser[] | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(rosterCacheKey(tenantKey))
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return null
    const users = parsed.filter((item) => item && typeof item === 'object') as TaskUser[]
    return users.length > 0 ? users : null
  } catch {
    return null
  }
}

const writeRosterCache = (tenantKey: string, users: TaskUser[]) => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(rosterCacheKey(tenantKey), JSON.stringify(users))
  } catch {
    // ignore
  }
}

const buildTaskUserNameLookup = (users: TaskUser[]): Map<string, string> => {
  const lookup = new Map<string, string>()
  users.forEach((user) => {
    lookup.set(user.id, user.name)
    ;(user.aliasIds ?? []).forEach((aliasId) => {
      if (aliasId) {
        lookup.set(aliasId, user.name)
      }
    })
  })
  return lookup
}

const generateId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `task-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

const normalizeTask = (task: Task): Task => ({
  ...task,
  collaboratorIds: Array.from(new Set(task.collaboratorIds))
})

const sortTasks = (tasks: Task[]): Task[] => {
  const byDueDate = (value: Task) => {
    if (!value.dueAt) return Number.MAX_SAFE_INTEGER
    return new Date(value.dueAt).getTime()
  }
  return tasks
    .slice()
    .sort((a, b) => {
      if (a.completed !== b.completed) {
        return a.completed ? 1 : -1
      }
      const timeA = byDueDate(a)
      const timeB = byDueDate(b)
      if (timeA === timeB) {
        return b.updatedAt.localeCompare(a.updatedAt)
      }
      return timeA - timeB
    })
}

const upsertTask = (task: Task, tasks: Task[]): Task[] => {
  const index = tasks.findIndex((item) => item.id === task.id)
  if (index === -1) {
    return [...tasks, normalizeTask(task)]
  }
  const next = tasks.slice()
  next[index] = normalizeTask(task)
  return next
}

const mergeCandidateIntoTasks = (candidate: Task, tasks: Task[]): Task[] => {
  const index = tasks.findIndex((item) => item.id === candidate.id)
  if (index === -1) {
    return [...tasks, candidate]
  }
  const next = tasks.slice()
  next[index] = candidate
  return next
}

const toTimestamp = (value?: string): number | null => {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }
  return parsed.getTime()
}

const milliseconds = {
  minute: 60 * 1000,
  hour: 60 * 60 * 1000,
  day: 24 * 60 * 60 * 1000
}

const formatDuration = (diffMs: number): string => {
  const abs = Math.abs(diffMs)
  if (abs >= milliseconds.day) {
    const days = Math.round(abs / milliseconds.day)
    return `${days} day${days === 1 ? '' : 's'}`
  }
  if (abs >= milliseconds.hour) {
    const hours = Math.round(abs / milliseconds.hour)
    return `${hours} hour${hours === 1 ? '' : 's'}`
  }
  const minutes = Math.max(1, Math.round(abs / milliseconds.minute))
  return `${minutes} minute${minutes === 1 ? '' : 's'}`
}

const formatDurationParts = (diffMs: number): TaskStatusDuration => {
  const abs = Math.abs(diffMs)
  if (abs >= milliseconds.day) {
    const count = Math.max(1, Math.round(abs / milliseconds.day))
    return { count, unitKey: count === 1 ? 'tasks.due.day' : 'tasks.due.days' }
  }
  if (abs >= milliseconds.hour) {
    const count = Math.max(1, Math.round(abs / milliseconds.hour))
    return { count, unitKey: count === 1 ? 'tasks.due.hour' : 'tasks.due.hours' }
  }
  const count = Math.max(1, Math.round(abs / milliseconds.minute))
  return { count, unitKey: count === 1 ? 'tasks.due.minute' : 'tasks.due.minutes' }
}

export const classifyTaskStatus = (task: Task, now: number = Date.now()): TaskStatusInsight => {
  if (task.completed) {
    const closedAt = task.completedAt ? toTimestamp(task.completedAt) : null
    const diff = closedAt ? now - closedAt : null
    const when = diff !== null ? formatDuration(diff) : null
    const duration = diff !== null ? formatDurationParts(diff) : undefined
    return {
      code: 'completed',
      label: 'Completed',
      labelKey: 'tasks.statusInsight.completed.label',
      description: when ? `Finished ${when} ago.` : 'Marked as complete.',
      descriptionKey: when
        ? 'tasks.statusInsight.completed.description.withAge'
        : 'tasks.statusInsight.completed.description.noAge',
      duration,
      palette: taskStatusPalette.completed
    }
  }

  const dueTime = toTimestamp(task.dueAt)

  if (!task.assigneeId) {
    return {
      code: 'blocked',
      label: 'Unassigned',
      labelKey: 'tasks.statusInsight.blocked.unassigned.label',
      description: 'Assign a responsible teammate to unblock progress.',
      descriptionKey: 'tasks.statusInsight.blocked.unassigned.description',
      palette: taskStatusPalette.blocked
    }
  }

  if (task.dueAt && dueTime === null) {
    return {
      code: 'blocked',
      label: 'Invalid due date',
      labelKey: 'tasks.statusInsight.blocked.invalidDue.label',
      description: 'Due date could not be parsed. Update the schedule.',
      descriptionKey: 'tasks.statusInsight.blocked.invalidDue.description',
      palette: taskStatusPalette.blocked
    }
  }

  if (dueTime !== null) {
    const delta = dueTime - now
    if (delta < 0) {
      const duration = formatDurationParts(delta)
      return {
        code: 'overdue',
        label: 'Overdue',
        description: `Past due by ${formatDuration(delta)}.`,
        labelKey: 'tasks.statusInsight.overdue.label',
        descriptionKey: 'tasks.statusInsight.overdue.description',
        duration,
        palette: taskStatusPalette.overdue
      }
    }
    if (delta <= 48 * milliseconds.hour) {
      const duration = formatDurationParts(delta)
      return {
        code: 'due_soon',
        label: 'Due soon',
        description: `Due in ${formatDuration(delta)}.`,
        labelKey: 'tasks.statusInsight.dueSoon.label',
        descriptionKey: 'tasks.statusInsight.dueSoon.description',
        duration,
        palette: taskStatusPalette.due_soon
      }
    }
  }

  return {
    code: 'on_track',
    label: 'On track',
    description: 'Assigned owner and no near-term deadline.',
    labelKey: 'tasks.statusInsight.onTrack.label',
    descriptionKey: 'tasks.statusInsight.onTrack.description',
    palette: taskStatusPalette.on_track
  }
}

const collectActiveDescendants = (tasks: Task[], rootId: string): Task[] => {
  const active: Task[] = []
  const queue: string[] = [rootId]
  const visited = new Set<string>(queue)

  while (queue.length > 0) {
    const current = queue.shift()!
    tasks.forEach((candidate) => {
      if (candidate.parentId === current && !visited.has(candidate.id)) {
        visited.add(candidate.id)
        queue.push(candidate.id)
        if (!candidate.completed) {
          active.push(candidate)
        }
      }
    })
  }

  return active
}

const ensureParentChainIsValid = (task: Task, tasks: Task[]): TaskValidationIssue[] => {
  const issues: TaskValidationIssue[] = []
  const lookup = new Map(tasks.map((existing) => [existing.id, existing]))

  if (!task.parentId) {
    return issues
  }

  const parent = lookup.get(task.parentId)
  if (!parent) {
    issues.push({
      field: 'parentId',
      severity: 'error',
      message: 'Selected parent task no longer exists.',
      messageKey: 'tasks.validation.parentMissing'
    })
    return issues
  }

  if (task.parentId === task.id) {
    issues.push({
      field: 'parentId',
      severity: 'error',
      message: 'A task cannot be its own parent.',
      messageKey: 'tasks.validation.parentSelf'
    })
    return issues
  }

  const seen = new Set<string>([task.id])
  let current: Task | undefined = parent
  while (current) {
    if (seen.has(current.id)) {
      issues.push({
        field: 'parentId',
        severity: 'error',
        message: 'Parent chain would create a cycle.',
        messageKey: 'tasks.validation.parentCycle'
      })
      break
    }
    seen.add(current.id)
    current = current.parentId ? lookup.get(current.parentId) : undefined
  }

  return issues
}

export const validateTaskDraft = (task: Task, options: TaskValidationOptions = {}): TaskValidationResult => {
  const now = options.now ?? Date.now()
  const tasks = options.tasks ?? []
  const issues: TaskValidationIssue[] = []

  const push = (issue: TaskValidationIssue) => {
    issues.push(issue)
  }

  const trimmedTitle = task.title.trim()
  if (trimmedTitle.length < 3) {
    push({
      field: 'title',
      severity: 'error',
      message: 'Title must be at least 3 characters long.',
      messageKey: 'tasks.validation.titleTooShort'
    })
  }

  if (task.description !== undefined) {
    const trimmedDescription = task.description.trim()
    if (trimmedDescription.length === 0) {
      push({
        field: 'description',
        severity: 'error',
        message: 'Description cannot be empty text.',
        messageKey: 'tasks.validation.descriptionEmpty'
      })
    }
  }

  if (task.dueAt) {
    const dueTime = toTimestamp(task.dueAt)
    if (dueTime === null) {
      push({
        field: 'dueAt',
        severity: 'error',
        message: 'Due date must be a valid ISO timestamp.',
        messageKey: 'tasks.validation.dueInvalid'
      })
    } else if (!task.completed && dueTime < now) {
      push({
        field: 'dueAt',
        severity: 'error',
        message: 'Active tasks need a due date in the future.',
        messageKey: 'tasks.validation.dueMustBeFutureForActive'
      })
    }
  }

  ensureParentChainIsValid(task, tasks).forEach(push)

  if (task.completed) {
    const activeDescendants = collectActiveDescendants(tasks, task.id)
    if (activeDescendants.length > 0) {
      const suffix = activeDescendants.length === 1 ? 'task is' : 'tasks are'
      const messageKey: TranslationKey =
        activeDescendants.length === 1
          ? 'tasks.validation.cannotCompleteWithActiveLinked.one'
          : 'tasks.validation.cannotCompleteWithActiveLinked.many'
      push({
        field: 'general',
        severity: 'error',
        message: `Cannot complete while ${activeDescendants.length} linked ${suffix} still active.`,
        messageKey,
        messageParams: { count: activeDescendants.length }
      })
    }
  }

  const hasValidDue = task.dueAt ? toTimestamp(task.dueAt) !== null : false
  if (!hasValidDue) {
    const childHasDue = tasks.some((candidate) => candidate.parentId === task.id && Boolean(candidate.dueAt))
    if (childHasDue) {
      push({
        field: 'dueAt',
        severity: 'warning',
        message: 'Linked tasks are scheduled; consider setting a due date on this objective.',
        messageKey: 'tasks.validation.objectiveConsiderDueDate'
      })
    }
  }

  return {
    issues,
    hasErrors: issues.some((issue) => issue.severity === 'error')
  }
}

const summarizeTasksForDashboard = (tasks: Task[]): TaskDashboardSummary => {
  const now = Date.now()
  const twoDaysMs = 48 * 60 * 60 * 1000
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000
  const lookup = new Map<string, Task>()
  const parentIds = new Set<string>()

  tasks.forEach((task) => {
    lookup.set(task.id, task)
    if (task.parentId) {
      parentIds.add(task.parentId)
    }
  })

  const userNameLookup = buildTaskUserNameLookup(readRosterCache(getActiveTenantKey()) ?? mockUsers)

  let totalActive = 0
  let overdue = 0
  let dueSoon = 0
  let unassigned = 0
  let standaloneActive = 0
  let completedLast7Days = 0

  const objectiveStats = new Map<string, {
    id: string
    title: string
    activeCount: number
    overdueCount: number
  }>()

  const activeTasks = tasks.filter((task) => !task.completed)
  totalActive = activeTasks.length

  activeTasks.forEach((task) => {
    const dueTime = toTimestamp(task.dueAt)
    if (dueTime !== null) {
      if (dueTime < now) {
        overdue += 1
      } else if (dueTime - now <= twoDaysMs) {
        dueSoon += 1
      }
    }

    if (!task.assigneeId) {
      unassigned += 1
    }

    const hasRealParent = task.parentId ? lookup.has(task.parentId) : false
    const isObjectiveParent = parentIds.has(task.id)
    if (!hasRealParent && !isObjectiveParent) {
      standaloneActive += 1
    }

    let ancestorId = task.parentId
    const visited = new Set<string>()
    while (ancestorId && !visited.has(ancestorId)) {
      visited.add(ancestorId)
      const ancestor = lookup.get(ancestorId)
      if (!ancestor) {
        break
      }
      const stat = objectiveStats.get(ancestor.id) ?? {
        id: ancestor.id,
        title: ancestor.title,
        activeCount: 0,
        overdueCount: 0
      }
      stat.activeCount += 1
      if (dueTime !== null && dueTime < now) {
        stat.overdueCount += 1
      }
      objectiveStats.set(ancestor.id, stat)
      ancestorId = ancestor.parentId && lookup.has(ancestor.parentId)
        ? ancestor.parentId
        : undefined
    }
  })

  tasks.forEach((task) => {
    if (!task.completed || !task.completedAt) return
    const completedTime = toTimestamp(task.completedAt)
    if (completedTime === null) return
    if (now - completedTime <= sevenDaysMs) {
      completedLast7Days += 1
    }
  })

  const activeObjectives = Array.from(objectiveStats.values())
    .sort((a, b) => {
      if (b.activeCount !== a.activeCount) return b.activeCount - a.activeCount
      if (b.overdueCount !== a.overdueCount) return b.overdueCount - a.overdueCount
      return a.title.localeCompare(b.title)
    })
    .slice(0, 4)

  const upcoming = activeTasks
    .map((task) => ({ task, dueTime: toTimestamp(task.dueAt) }))
    .filter((entry): entry is { task: Task; dueTime: number } => entry.dueTime !== null && entry.dueTime >= now)
    .sort((a, b) => a.dueTime - b.dueTime)
    .slice(0, 3)
    .map(({ task }) => ({
      id: task.id,
      title: task.title,
      dueAt: task.dueAt,
      assigneeName: task.assigneeId ? userNameLookup.get(task.assigneeId) : undefined
    }))

  const overdueItems = activeTasks
    .map((task) => ({ task, dueTime: toTimestamp(task.dueAt) }))
    .filter((entry): entry is { task: Task; dueTime: number } => entry.dueTime !== null && entry.dueTime < now)
    .sort((a, b) => a.dueTime - b.dueTime)
    .slice(0, 3)
    .map(({ task }) => ({
      id: task.id,
      title: task.title,
      dueAt: task.dueAt,
      assigneeName: task.assigneeId ? userNameLookup.get(task.assigneeId) : undefined
    }))

  return {
    totalActive,
    overdue,
    dueSoon,
    unassigned,
    standaloneActive,
    completedLast7Days,
    activeObjectives,
    upcoming,
    overdueItems,
    updatedAt: new Date().toISOString()
  }
}

export const taskApi = {
  async listTasks(): Promise<Task[]> {
    await ensureOfflineReady()
    try {
      const remote = await api.tasks.list()
      const mapped = remote.map(fromApiTask)
      const normalized = sortTasks(mapped)
      await dexieTaskRepo.replaceAll(normalized)
      lastTaskListSource = 'remote'
      return clone(normalized)
    } catch (error) {
      const cached = sortTasks(await dexieTaskRepo.list())
      if (cached.length > 0) {
        if (import.meta.env.DEV) {
          console.warn('[taskApi] falling back to cached tasks', error)
        }
        lastTaskListSource = 'cache'
        return clone(cached)
      }
      // As a last resort, fall back to legacy localStorage (should be rare because
      // offline init migrates it into IndexedDB).
      const legacy = sortTasks(loadTasks())
      if (legacy.length > 0) {
        await dexieTaskRepo.replaceAll(legacy)
        lastTaskListSource = 'cache'
        return clone(legacy)
      }

      throw error
    }
  },

  async createTask(payload: CreateTaskPayload): Promise<TaskMutationResult<Task>> {
    await ensureOfflineReady()
    const existing = await dexieTaskRepo.list()
    const nowIso = new Date().toISOString()
    const candidate: Task = normalizeTask({
      id: generateId(),
      title: payload.title.trim(),
      description: payload.description?.trim() || undefined,
      tags: payload.tags ?? [],
      assigneeId: payload.assigneeId,
      collaboratorIds: payload.collaboratorIds?.slice() ?? [],
      dueAt: payload.dueAt,
      completed: false,
      createdAt: nowIso,
      updatedAt: nowIso,
      parentId: payload.parentId,
      reminderSettings: {
        ...defaultReminder,
        ...payload.reminderSettings
      },
      metadata: payload.metadata ? { ...payload.metadata } : undefined
    })

    const validation = validateTaskDraft(candidate, { tasks: [...existing, candidate] })
    if (validation.hasErrors) {
      const message = validation.issues
        .filter((issue) => issue.severity === 'error')
        .map((issue) => issue.message)
        .join(' ')
      throw new Error(message || 'Task validation failed.')
    }

    const apiPayload: TaskCreatePayload = {
      id: candidate.id,
      title: candidate.title,
      description: candidate.description,
      tags: candidate.tags,
      assigneeId: candidate.assigneeId,
      collaboratorIds: candidate.collaboratorIds,
      dueAt: candidate.dueAt,
      reminderSettings: candidate.reminderSettings,
      parentId: candidate.parentId,
      metadata: candidate.metadata
    }

    // Offline-first: persist locally immediately.
    const nextLocal = sortTasks(upsertTask(candidate, existing))
    await dexieTaskRepo.replaceAll(nextLocal)

    try {
      await enqueueEntityChange({
        entityType: 'task',
        op: 'upsert',
        entityId: candidate.id,
        payload: candidate
      })
    } catch {
      // best-effort
    }

    void dexieActivityRepo.log({
      source: 'task',
      level: 'success',
      title: 'Task created',
      detail: candidate.title,
      farmId: candidate.metadata && typeof candidate.metadata.farmId === 'string' ? candidate.metadata.farmId : undefined,
      productionUnitId: candidate.metadata && typeof candidate.metadata.productionUnitId === 'string' ? candidate.metadata.productionUnitId : undefined,
      lotId: candidate.metadata && typeof candidate.metadata.lotId === 'string' ? candidate.metadata.lotId : undefined,
      dedupeKey: `task:create:${candidate.id}`,
      dedupeWindowMs: 10_000
    }).catch(() => {})

    try {
      const createdRecord = await api.tasks.create(apiPayload)
      const created = fromApiTask(createdRecord)
      const next = sortTasks(upsertTask(created, nextLocal))
      await dexieTaskRepo.replaceAll(next)
      await clearEntityChange('task', candidate.id)
      lastTaskListSource = 'remote'
      return syncedEntityMutationResult(clone(created))
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[taskApi] createTask saved offline (pending sync)', error)
      }
      lastTaskListSource = 'cache'
      return buildQueuedEntityMutationResult(clone(candidate), {
        entityType: 'task',
        entityId: candidate.id
      })
    }
  },

  async updateTask(payload: UpdateTaskPayload): Promise<TaskMutationResult<Task>> {
    await ensureOfflineReady()
    const tasks = await dexieTaskRepo.list()
    const existing = tasks.find((item) => item.id === payload.id)
    if (!existing) {
      throw new Error('Task not found')
    }

    const nowIso = new Date().toISOString()
    const titleInput = payload.title
    const descriptionInput = payload.description
    const tagsInput = payload.tags
    const assigneeInput = payload.assigneeId
    const collaboratorInput = payload.collaboratorIds
    const dueAtInput = payload.dueAt
    const parentInput = payload.parentId
    const reminderInput = payload.reminderSettings
    const completedInput = payload.completed
    const completedAtInput = payload.completedAt
    const metadataInput = payload.metadata

    const nextTitle = titleInput !== undefined ? titleInput.trim() : existing.title
    const nextDescription = descriptionInput === undefined
      ? existing.description
      : descriptionInput === null
        ? undefined
        : descriptionInput.trim() || undefined
    const nextAssigneeId = assigneeInput === undefined
      ? existing.assigneeId
      : assigneeInput === null
        ? undefined
        : assigneeInput.trim() || undefined
    const nextTags = tagsInput === undefined ? existing.tags : tagsInput
    const nextCollaborators = collaboratorInput ?? existing.collaboratorIds
    const nextDueAt = dueAtInput === undefined ? existing.dueAt : dueAtInput || undefined
    const nextParentId = parentInput === undefined ? existing.parentId : parentInput || undefined
    const nextReminderSettings = reminderInput
      ? {
          ...existing.reminderSettings,
          ...reminderInput
        }
      : existing.reminderSettings

    let nextCompletedAt = completedAtInput === undefined ? existing.completedAt : completedAtInput || undefined
    let completionForApi: string | null | undefined = completedAtInput

    if (completedInput !== undefined) {
      if (completedInput) {
        const stamp = nextCompletedAt ?? existing.completedAt ?? nowIso
        nextCompletedAt = stamp
        completionForApi = stamp
      } else {
        nextCompletedAt = undefined
        completionForApi = null
      }
    }

    const nextCompleted = completedInput ?? existing.completed

    const nextMetadata = metadataInput === undefined
      ? existing.metadata
      : Object.keys(metadataInput).length > 0
        ? { ...metadataInput }
        : undefined

    const updated: Task = normalizeTask({
      ...existing,
      title: nextTitle,
      description: nextDescription,
      tags: nextTags,
      assigneeId: nextAssigneeId,
      collaboratorIds: nextCollaborators,
      dueAt: nextDueAt,
      parentId: nextParentId,
      reminderSettings: nextReminderSettings,
      completed: nextCompleted,
      completedAt: nextCompletedAt,
      metadata: nextMetadata,
      updatedAt: nowIso
    })

    const nextSnapshot = mergeCandidateIntoTasks(updated, tasks)
    const validation = validateTaskDraft(updated, { tasks: nextSnapshot })
    if (validation.hasErrors) {
      const message = validation.issues
        .filter((issue) => issue.severity === 'error')
        .map((issue) => issue.message)
        .join(' ')
      throw new Error(message || 'Task validation failed.')
    }

    const apiPayload: TaskUpdatePayload = {}
    if (titleInput !== undefined) apiPayload.title = nextTitle
    if (descriptionInput !== undefined) apiPayload.description = descriptionInput === null ? null : nextDescription ?? null
    if (tagsInput !== undefined) apiPayload.tags = tagsInput
    if (assigneeInput !== undefined) apiPayload.assigneeId = assigneeInput === null ? null : nextAssigneeId ?? null
    if (collaboratorInput !== undefined) apiPayload.collaboratorIds = collaboratorInput
    if (dueAtInput !== undefined) apiPayload.dueAt = dueAtInput
    if (parentInput !== undefined) apiPayload.parentId = parentInput
    if (reminderInput !== undefined) apiPayload.reminderSettings = reminderInput
    if (metadataInput !== undefined) apiPayload.metadata = { ...metadataInput }
    if (completedInput !== undefined) {
      apiPayload.completed = completedInput
      apiPayload.completedAt = completionForApi
    } else if (completedAtInput !== undefined) {
      apiPayload.completedAt = completionForApi
    }

    // Offline-first: persist locally immediately.
    const nextLocal = sortTasks(upsertTask(updated, tasks))
    await dexieTaskRepo.replaceAll(nextLocal)

    try {
      await enqueueEntityChange({
        entityType: 'task',
        op: 'upsert',
        entityId: updated.id,
        payload: updated
      })
    } catch {
      // best-effort
    }

    const completionChanged = (payload.completed !== undefined) && payload.completed !== existing.completed
    const title = completionChanged
      ? payload.completed
        ? 'Task completed'
        : 'Task reopened'
      : 'Task updated'

    void dexieActivityRepo.log({
      source: 'task',
      level: completionChanged ? 'success' : 'info',
      title,
      detail: updated.title,
      farmId: updated.metadata && typeof updated.metadata.farmId === 'string' ? updated.metadata.farmId : undefined,
      productionUnitId: updated.metadata && typeof updated.metadata.productionUnitId === 'string' ? updated.metadata.productionUnitId : undefined,
      lotId: updated.metadata && typeof updated.metadata.lotId === 'string' ? updated.metadata.lotId : undefined,
      dedupeKey: `task:update:${updated.id}`,
      dedupeWindowMs: 30_000
    }).catch(() => {})

    try {
      const updatedRecord = await api.tasks.update(payload.id, apiPayload)
      const updatedFromApi = fromApiTask(updatedRecord)
      const next = sortTasks(upsertTask(updatedFromApi, nextLocal))
      await dexieTaskRepo.replaceAll(next)
      await clearEntityChange('task', updated.id)
      lastTaskListSource = 'remote'
      return syncedEntityMutationResult(clone(updatedFromApi))
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[taskApi] updateTask saved offline (pending sync)', error)
      }
      lastTaskListSource = 'cache'
      return buildQueuedEntityMutationResult(clone(updated), {
        entityType: 'task',
        entityId: updated.id
      })
    }
  },

  async deleteTask(id: string): Promise<TaskMutationResult<void>> {
    await ensureOfflineReady()
    const tasks = await dexieTaskRepo.list()
    const existing = tasks.find((task) => task.id === id)
    const nextLocal = sortTasks(tasks.filter((task) => task.id !== id))
    await dexieTaskRepo.replaceAll(nextLocal)

    try {
      await enqueueEntityChange({
        entityType: 'task',
        op: 'delete',
        entityId: id
      })
    } catch {
      // best-effort
    }

    void dexieActivityRepo.log({
      source: 'task',
      level: 'warning',
      title: 'Task deleted',
      detail: existing?.title,
      farmId: existing?.metadata && typeof existing.metadata.farmId === 'string' ? existing.metadata.farmId : undefined,
      productionUnitId: existing?.metadata && typeof existing.metadata.productionUnitId === 'string' ? existing.metadata.productionUnitId : undefined,
      lotId: existing?.metadata && typeof existing.metadata.lotId === 'string' ? existing.metadata.lotId : undefined,
      dedupeKey: `task:delete:${id}`,
      dedupeWindowMs: 10_000
    }).catch(() => {})

    try {
      await api.tasks.delete(id)
      await clearEntityChange('task', id)
      lastTaskListSource = 'remote'
      return syncedEntityMutationResult(undefined)
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[taskApi] deleteTask applied offline (pending sync)', error)
      }
      lastTaskListSource = 'cache'
      return buildQueuedEntityMutationResult(undefined, {
        entityType: 'task',
        entityId: id
      })
    }
  },

  async listUsers(): Promise<TaskUser[]> {
    const tenantKey = getActiveTenantKey()

    try {
      const members = await api.teamMembers.list()
      const users: TaskUser[] = members.map((member) => ({
        id: member.tenantMembershipId ?? member.id,
        name: member.label,
        email: '',
        role: member.role,
        team: member.tenantKey,
        actorId: member.actorId,
        userId: member.userId ?? undefined,
        tenantMembershipId: member.tenantMembershipId ?? member.id,
        aliasIds: member.legacyTokenIds.filter((value) => value && value !== (member.tenantMembershipId ?? member.id))
      }))
      writeRosterCache(tenantKey, users)
      return clone(users)
    } catch {
      const cached = readRosterCache(tenantKey)
      if (cached) return clone(cached)
      return clone(mockUsers)
    }
  },

  async readTaskSyncStatus(taskIds: string[]): Promise<EntityBatchSyncStatus> {
    return readEntityBatchSyncStatus('task', taskIds)
  },

  async retryTaskSyncStatus(taskIds: string[]): Promise<EntityBatchSyncStatus> {
    return retryEntityBatchSyncStatus({
      entityType: 'task',
      entityIds: taskIds,
      maxEntries: 25,
      maxPasses: 5
    })
  },

  async retryTaskSyncStatusWithReport(taskIds: string[]): Promise<EntityBatchSyncRetryReport> {
    return retryEntityBatchSyncStatusWithReport({
      entityType: 'task',
      entityIds: taskIds,
      maxEntries: 25,
      maxPasses: 5
    })
  },

  getLastListSource(): TaskListSource | null {
    return lastTaskListSource
  },

  async getDashboardSummary(): Promise<TaskDashboardSummary> {
    await ensureOfflineReady()
    const tasks = await dexieTaskRepo.list()
    return summarizeTasksForDashboard(tasks)
  },

  async notifyAssignee(task: Task): Promise<void> {
    const clarity = evaluateTaskClarity(task.title)
    console.info('[task] notify assignee', {
      id: task.id,
      title: task.title,
      assigneeId: task.assigneeId,
      clarityScore: clarity.score
    })
  },

  async notifyTeam(task: Task): Promise<void> {
    console.info('[task] notify team', {
      id: task.id,
      collaborators: task.collaboratorIds
    })
  },

  async scheduleReminder(task: Task): Promise<void> {
    console.info('[task] schedule reminder', {
      id: task.id,
      dueAt: task.dueAt,
      cadenceMinutes: task.reminderSettings.cadenceMinutes
    })
  },

  classifyStatus(task: Task): TaskStatusInsight {
    return classifyTaskStatus(task)
  },

  validateDraft(task: Task, options?: TaskValidationOptions): TaskValidationResult {
    return validateTaskDraft(task, options)
  },

  evaluateClarity: evaluateTaskClarity
}

export type TaskApi = typeof taskApi

export { summarizeTasksForDashboard }
